import { useTranslation } from 'react-i18next';
import { Button, Grid, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PaymentsOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import React from 'react';
import { useEditOrder } from '../../EditOrderContext';
import { findMultipleTicketsUiObject, readMultipleTicketsQuantities } from '../../../../../common/multipleTicketsUtils';
import { TicketBreakdownDisplay } from '../../../../../editOrderUiObjects/TicketBreakdownDisplay';
import { OrderData, VoucherType } from '../../../../../api';
import { usePriceRenderer } from '../../../../../common/usePriceRenderer';
import { usePriceFormatter } from '../../../../../common/usePriceFormatter';
import { visibleCatalogCategories } from '../../../../../catalog/catalogUtils';
import { UseAuthPermissions } from '../../../../../session/UseAuthPermissions';
import { useCatalogCategories } from '../../../../../catalog/swr/useCatalogCategories';
import { ExtraItemsQuickAction } from './ExtraItemsQuickAction';

export const MAX_SUMMARY_ITEMS = 6;

type ExperienceDetailSummaryProps = {
  hidePricing?: boolean;
  isPanelExpanded?: boolean;
  onQuickAction?: (event: React.SyntheticEvent, type: ExtraItemsQuickAction) => void;
};
export const ExperienceDetailSummary = ({
  hidePricing,
  isPanelExpanded,
  onQuickAction,
}: ExperienceDetailSummaryProps) => {
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const { t } = useTranslation();
  const priceFormatter = usePriceFormatter();
  const { SlimPrice } = usePriceRenderer({
    orderOrVoucher: editOrderState.orderOrVoucher,
    initialPrice: editOrderState.details.price,
    returningCustomerFromOrderId: editOrderState.returningCustomerFromOrderId,
    extraPaymentLines: editOrderState.extraPaymentsLines,
    refreshKey: editOrderState.paymentLinksRefreshKey,
  });
  const multipleTicketsUiObject = findMultipleTicketsUiObject(editOrderState.orderPage);
  const multipleTicketsQuantities = readMultipleTicketsQuantities(editOrderState.details.custom_details);
  const hasMultipleTicketsBreakdown =
    !!multipleTicketsUiObject && Object.values(multipleTicketsQuantities).some((v) => v > 0);
  const order = editOrderState.orderOrVoucher as OrderData | undefined;
  const items = order?.extra_items ?? [];
  const balanceDue = order?.balance_due ?? 0;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { readOnly } = UseAuthPermissions();
  const { categories } = useCatalogCategories();
  const hasOrder = !!order?.id;
  const hasCatalog = categories.some((category) => !category.archived);
  // Same soft scoping as the add-item picker: only when this workshop has a catalog category of its own
  // (or one that covers every workshop).
  const hasCatalogForPage = visibleCatalogCategories(categories, editOrderState.orderPage?.id).length > 0;
  const canAddItems = hasOrder && !readOnly && hasCatalogForPage;
  // Payment lives in ExtraItemsSection, which only renders with a catalog or existing items.
  const canPay = hasOrder && !readOnly && balanceDue > 0 && (hasCatalog || items.length > 0);
  const showQuickActions = !isPanelExpanded && !!onQuickAction && (canAddItems || canPay);
  const quickActions = [
    canAddItems && {
      type: ExtraItemsQuickAction.ADD_ITEMS,
      label: t('calendar.edit_order.quick.addItems'),
      icon: <ShoppingCartOutlined fontSize="small" />,
      color: 'inherit' as const,
    },
    canPay && {
      type: ExtraItemsQuickAction.PAYMENT,
      label: t('calendar.edit_order.quick.payment'),
      icon: <PaymentsOutlined fontSize="small" />,
      color: 'primary' as const,
    },
  ].filter(Boolean) as {
    type: ExtraItemsQuickAction;
    label: string;
    icon: React.ReactElement;
    color: 'inherit' | 'primary';
  }[];
  const renderQuickActions = () => (
    <Grid container width="auto" alignItems="center" gap={1} wrap="nowrap" flexShrink={0}>
      {quickActions.map((action) => {
        const handleClick = (event: React.MouseEvent) => {
          // The summary is the accordion's toggle: keep the click from also collapsing/expanding it.
          event.stopPropagation();
          onQuickAction?.(event, action.type);
        };
        return isSmallScreen ? (
          <Tooltip key={action.type} title={action.label}>
            <IconButton
              size="small"
              aria-label={action.label}
              color={action.color}
              onClick={handleClick}
              onFocus={(event) => event.stopPropagation()}
              sx={{ minHeight: 32, minWidth: 32, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {action.icon}
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            key={action.type}
            size="small"
            variant="outlined"
            color={action.color}
            startIcon={action.icon}
            onClick={handleClick}
            sx={{ minHeight: 32, textTransform: 'none', whiteSpace: 'nowrap' }}>
            {action.label}
          </Button>
        );
      })}
    </Grid>
  );
  return (
    <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap" gap={1}>
      <Grid container direction="column" gap={1} sx={{ minWidth: 0, flex: 1 }}>
        <Grid container columnGap={3} rowGap={0.5} alignItems="center">
          {editOrderState.voucherType === VoucherType.MONETARY ? (
            <Grid container width="auto" alignItems="center" gap={0.75} wrap="nowrap">
              <Typography variant="body1">{`${t('vouchers.amount')}:`}</Typography>
              <Typography variant="body1" dir="ltr">
                {priceFormatter(editOrderState.details.price)}
              </Typography>
            </Grid>
          ) : hasMultipleTicketsBreakdown ? (
            <TicketBreakdownDisplay
              quantities={multipleTicketsQuantities}
              orderPage={editOrderState.orderPage}
              variant="compact"
            />
          ) : (
            <Typography variant="body1">
              {t('calendar.edit_order.participants', { number: editOrderState.details.persons })}
            </Typography>
          )}
          {!hidePricing && (
            <Grid container width="auto" alignItems="center" gap={0.75} wrap="nowrap">
              <Typography variant="body1">
                {t('calendar.edit_order.price', { number: editOrderState.details.price })}
              </Typography>
              <SlimPrice />
            </Grid>
          )}
          {!hidePricing && balanceDue > 0 && (
            <Grid container width="auto" alignItems="center" gap={0.75} wrap="nowrap">
              <Typography variant="body1">{`${t(
                'calendar.edit_order.order_details.extraItems.balanceDue',
              )}:`}</Typography>
              <Typography variant="body1" fontWeight={700} color="warning.dark" dir="ltr">
                {priceFormatter(balanceDue)}
              </Typography>
            </Grid>
          )}
        </Grid>
        {!!items.length && (
          <Typography variant="body2" color="text.secondary">
            {items.slice(0, MAX_SUMMARY_ITEMS).map((line, index) => (
              <React.Fragment key={line.id}>
                {index > 0 && <span aria-hidden>{'  ·  '}</span>}
                <Typography component="span" variant="body2" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography component="span" variant="body2" fontWeight={700} dir="ltr">
                    {`${line.quantity}×`}
                  </Typography>{' '}
                  {line.label}
                </Typography>
              </React.Fragment>
            ))}
            {items.length > MAX_SUMMARY_ITEMS && (
              <>
                {'  ·  '}
                {t('calendar.edit_order.summary.moreItems', { count: items.length - MAX_SUMMARY_ITEMS })}
              </>
            )}
          </Typography>
        )}
      </Grid>
      {showQuickActions && renderQuickActions()}
    </Grid>
  );
};
